import dispatchActionForAllModules from '@/src/utils/dispatch-action-for-all-modules'
import axios from 'axios'
import design from '@design'
import { locales } from '@constants/lang'
import wizard from '@constants/wizard/index'

export const state = {
  currentUser: getSavedState('auth.currentUser'),
  currentPatient: getSavedState('auth.currentPatient'),
  currentProfessional: null,
  showProfile: getSavedKey('showProfile'),
  wizard: getSavedState('wizard'),
  sessionAlert: false,
}

export const getters = {
  // Whether the user is currently logged in.
  loggedIn(state) {
    return !!state.currentUser
  },
  fullAccess(state) {
    return (
      !!state.currentUser &&
      parseJwt(state.currentUser.access).permissions === 'full'
    )
  },
  limitedAccess(state) {
    return (
      !!state.currentUser &&
      parseJwt(state.currentUser.access).permissions === 'limited'
    )
  },
  accessClaims(state) {
    return state.currentUser ? parseJwt(state.currentUser.access) : ''
  },
  refreshClaims(state) {
    return state.currentUser ? parseJwt(state.currentUser.refresh) : ''
  },
  accessToken(state) {
    return state.currentUser ? state.currentUser.access : null
  },
  serializedUser(state) {
    return state.currentUser ? state.currentUser.user : null
  },
  getPatient(state) {
    return state.currentPatient ? state.currentPatient : null
  },
  getProfessional(state) {
    return state.currentProfessional ? state.currentProfessional : null
  },
  getShowProfile(state) {
    return state.showProfile || null
  },
  getWizard(state) {
    return state.wizard ? state.wizard : null
  },
  hasMfa(state) {
    return (
      state.currentUser &&
      state.currentUser.user &&
      state.currentUser.user.has_mfa_enabled
    )
  },
  isMfaGoogle(state, getters) {
    if (!state.currentUser) return null
    let isMfaGoogle = false

    state.currentUser.user.mfa_devices.forEach((device) => {
      if (device.device_type === 'google') {
        isMfaGoogle = true
      }
    })

    return isMfaGoogle
  },
  isMfaEmail(state, getters) {
    if (!state.currentUser) return null
    let isMfaEmail = false

    state.currentUser.user.mfa_devices.forEach((device) => {
      if (device.device_type === 'email') {
        isMfaEmail = true
      }
    })

    return isMfaEmail
  },
  defaultProfilePhoto(state, getters) {
    if (!getters.serializedUser) return null
    return (
      'https://avatars.dicebear.com/api/initials/' +
      getters.serializedUser.first_name +
      '-' +
      getters.serializedUser.last_name +
      `.svg?background=${encodeURIComponent(design['global-theme-primary'])}`
    )
  },
  // settings
  settings(state) {
    return state.currentUser && state.currentUser.user
      ? state.currentUser.user.settings
      : null
  },
  darkMode(state, getters) {
    return getters.settings ? getters.settings.dark : false
  },
  language(state, getters) {
    return getters.settings
      ? getLocale(getters.settings.language, locales, locales[0].code)
      : 'en'
  },
  isWizardComplete(state, getters) {
    return getters.serializedUser.wizard_complete || null
  },
  isWizardMinimized(state, getters) {
    return getters.settings
      ? getters.settings.isWizardMinimized || false
      : false
  },
  wizardStatus(state, getters) {
    if (getters.isWizardComplete) {
      return wizard.status.COMPLETED
    } else if (getters.isWizardMinimized) {
      return wizard.status.MINIMIZED
    } else {
      return wizard.status.PENDING
    }
  },
}

export const mutations = {
  SET_CURRENT_USER(state, newValue) {
    state.currentUser = newValue
    saveState('auth.currentUser', newValue)
    setDefaultAuthHeaders(state)
  },
  SET_CURRENT_PATIENT(state, newValue) {
    state.currentPatient = newValue
    saveState('auth.currentPatient', newValue)
  },
  SET_CURRENT_PROFESSIONAL(state, newValue) {
    state.currentProfessional = newValue
  },
  SET_SHOW_PROFILE(state, newValue) {
    state.showProfile = newValue
    saveKey('showProfile', newValue)
  },
  SET_WIZARD(state, newValue) {
    state.wizard = newValue
    saveState('wizard', newValue)
  },
  SET_SESSION_ALERT(state, newValue) {
    state.sessionAlert = newValue
  },
}

export const actions = {
  // This is automatically run in `src/state/store.js` when the app
  // starts, along with any other actions named `init` in other modules.
  init({ state, dispatch }) {
    setDefaultAuthHeaders(state)
    return dispatch('validate').then((user) => {
      if (user) dispatch('fetchUser')
    })
  },
  // Destroy action called on logout,
  // along with any other actions named `destroy` in other modules
  destroy({ commit, state, dispatch }) {
    removeKey('showProfile')
    removeKey('messageStatus')
    commit('SET_CURRENT_USER', null)
    commit('SET_CURRENT_PATIENT', null)
    commit('SET_CURRENT_PROFESSIONAL', null)
    commit('SET_SHOW_PROFILE', null)
    commit('SET_WIZARD', null)
    commit('SET_SESSION_ALERT', false)
  },

  // Who Am I
  fetchUser({ commit, state, dispatch }, payload) {
    return axios
      .get('/api/v1/me/')
      .then(({ data }) => {
        localStorage.setItem('meresponse', JSON.stringify(data))
        const user = {
          access: state.currentUser.access,
          refresh: state.currentUser.refresh,
          user: data,
        }
        commit('SET_CURRENT_USER', user)
        return user
      })
      .catch((err) => console.warn(err))
  },

  // Updates Current User
  updateUser({ commit, dispatch }, payload) {
    return axios
      .put('/api/v1/me/', payload)
      .then((response) => {
        dispatch('fetchUser')
      })
      .catch((err) => console.warn(err))
  },

  // Updates Current User's Settings
  updateUserSettings({ state, commit }, payload) {
    let { settings } = JSON.parse(JSON.stringify(state.currentUser.user))
    if (!settings) settings = {}
    Object.keys(payload).forEach((key) => {
      settings[key] = payload[key]
    })
    return axios
      .put('/api/v1/me/', { settings })
      .then(({ data }) => {
        const user = {
          access: state.currentUser.access,
          refresh: state.currentUser.refresh,
          user: data,
        }
        commit('SET_CURRENT_USER', user)
        return user
      })
      .catch((err) => console.warn(err))
  },
  // Logs in the current user.
  logIn({ commit, dispatch, getters }, { email, password, org } = {}) {
    if (getters.loggedIn) return dispatch('validate')

    return axios
      .post('/api/v1/login/', { email, password, org })
      .then((response) => {
        // console.log(response)
        const user = response.data
        commit('SET_CURRENT_USER', user)
        return user
      })
      .catch((err) => {
        console.warn(err)
        return err
      })
  },

  // Logs out the current user.
  logOut({ commit }) {
    return axios
      .post('/api/v1/logout/', { refresh: state.currentUser.refresh })
      .then((response) => {
        dispatchActionForAllModules('destroy')
      })
      .catch((err) => {
        console.warn(err)
        return null
      })
  },

  // Validates the current user's token and refreshes it
  // with new data from the API.
  validate({ commit, state, dispatch }) {
    if (!state.currentUser) return Promise.resolve(null)

    return axios
      .post('/api/v1/refresh/', { refresh: state.currentUser.refresh })
      .then((response) => {
        const user = {
          access: response.data.access,
          refresh: state.currentUser.refresh,
          user: state.currentUser.user,
        }
        commit('SET_CURRENT_USER', user)
        return user
      })
      .catch((error) => {
        if (error.response && error.response.status === 401) {
          dispatchActionForAllModules('destroy')
        } else {
          console.warn(error)
        }
        return null
      })
  },

  setCurrentUser({ commit, state }, payload) {
    if (payload) {
      commit('SET_CURRENT_USER', payload)
      return payload
    }
    return null
  },

  setSessionAlertStatus({ commit, state }, payload) {
    commit('SET_SESSION_ALERT', payload)
    return payload
  },

  setCurrentPatient({ commit, state }, payload) {
    if (payload) {
      commit('SET_CURRENT_PATIENT', payload)
      return payload
    }
    return null
  },

  setCurrentProfessional({ commit, state }, payload) {
    if (payload) {
      commit('SET_CURRENT_PROFESSIONAL', payload)
      return payload
    }
    return null
  },

  setShowProfile({ commit, state }, payload) {
    if (payload) {
      commit('SET_SHOW_PROFILE', payload)
      return payload
    }
    return null
  },

  addMfaDevice({ commit, state }, payload) {
    if (payload) {
      const { user } = JSON.parse(JSON.stringify(state.currentUser)) // clone user from state - cannot mutate it directly
      user.mfa_devices.push(payload)
      user.has_mfa_enabled = !!user.mfa_devices.length
      // create updated user model
      const updatedUser = {
        access: state.currentUser.access,
        refresh: state.currentUser.refresh,
        user: user,
      }
      commit('SET_CURRENT_USER', updatedUser)
      return updatedUser
    }
    return null
  },

  removeMfaDevice({ commit, state }, payload) {
    if (payload) {
      const { user } = JSON.parse(JSON.stringify(state.currentUser))
      user.mfa_devices = user.mfa_devices.filter(
        (device) => device.uuid !== payload.id
      )
      user.has_mfa_enabled = !!user.mfa_devices.length
      // create updated user model
      const updatedUser = {
        access: state.currentUser.access,
        refresh: state.currentUser.refresh,
        user: user,
      }
      commit('SET_CURRENT_USER', updatedUser)
      return updatedUser
    }
    return null
  },
  setProfilePicture({ commit, state }, payload) {
    if (payload) {
      const { user } = JSON.parse(JSON.stringify(state.currentUser))
      user.profile_picture = payload.photo
      const updatedUser = {
        access: state.currentUser.access,
        refresh: state.currentUser.refresh,
        user: user,
      }
      commit('SET_CURRENT_USER', updatedUser)
      return updatedUser
    }
    return null
  },
  removeProfilePicture({ commit, state }) {
    const { user } = JSON.parse(JSON.stringify(state.currentUser))
    user.profile_picture = null
    const updatedUser = {
      access: state.currentUser.access,
      refresh: state.currentUser.refresh,
      user: user,
    }
    commit('SET_CURRENT_USER', updatedUser)
    return updatedUser
  },
  async fetchWizardData({ commit, state }) {
    try {
      const { data } = await axios.get('/api/v1/wizard/')
      commit('SET_WIZARD', data)
      return data
    } catch (error) {
      console.warn(error)
    }
  },
  async updateWizardStep({ commit, state }, step) {
    try {
      const { data } = await axios.post('/api/v1/wizard/', {
        model_name: state.wizard.steps[step].model_name,
      })
      commit('SET_WIZARD', data)
      return data
    } catch (error) {
      console.warn(error)
    }
  },
}

// ===
// Private helpers
// ===

// todo: later encode to base64 string with btoa and decode with atob

function getSavedState(key) {
  return JSON.parse(window.localStorage.getItem(key))
}

function getSavedKey(key) {
  if (Object.prototype.hasOwnProperty.call(window.localStorage, key)) {
    return window.localStorage.getItem(key)
  }
  return null
}

function saveState(key, state) {
  window.localStorage.setItem(key, JSON.stringify(state))
}

function saveKey(key, value) {
  window.localStorage.setItem(key, value)
}

function removeKey(key) {
  if (Object.prototype.hasOwnProperty.call(window.localStorage, key)) {
    window.localStorage.removeItem(key)
  }
}

function setDefaultAuthHeaders(state) {
  axios.defaults.headers.common.Authorization = state.currentUser
    ? `Bearer ${state.currentUser.access}`
    : ''
}

function parseJwt(token) {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace('-', '+').replace('_', '/')
  return JSON.parse(window.atob(base64))
}

/**
 * @param  {String[]} locales
 * @param  {String} fallback
 * @return {String}
 */
function getLocale(locale, locales, fallback) {
  if (locales.some((el) => el.code === locale)) {
    // check if it exists in predefined array of locales
    return locale
  } else if (locale) {
    // if it doesn't but is set manually delete it for security
    return fallback
  }
  // finally
  return fallback
}
